import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import '../styles/producto.css';
import { useCarrito } from '../context/carritoContext';
import { getComboById } from '../services/catalogApi';
import CombosHome from './CombosHome';
import Seo from './Seo';
import { absoluteUrl } from '../config/seo';
import { productoPath } from '../utils/slug';

const PaginaCombo = () => {
  const { id } = useParams();
  const [combo, setCombo] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [agregado, setAgregado] = useState(false);

  const navigate = useNavigate();
  const { agregarAlCarrito, carrito } = useCarrito();

  const handleBackClick = () => navigate(-1);

  useEffect(() => {
    const cargarCombo = async () => {
      setLoading(true);
      try {
        const encontrado = await getComboById(id);
        if (!encontrado) {
          setError('Combo no encontrado');
          setCombo(null);
          return;
        }
        setCombo(encontrado);
      } catch {
        setError('No se pudo cargar el combo');
      } finally {
        setLoading(false);
      }
    };

    cargarCombo();
  }, [id]);

  if (loading) return <p>Cargando combo...</p>;
  if (error) return <p>{error}</p>;
  if (!combo) return <p>Combo no encontrado</p>;

  const fotoRaw = combo.comboFoto;
  const foto = (Array.isArray(fotoRaw) ? fotoRaw[0] : fotoRaw) || '/img/default.jpg';
  const precioFinal = Math.max(0, combo.comboPrecio - combo.comboDescuento);
  const totalArticulos = combo.productos.reduce((acc, item) => acc + (item.cantidad || 1), 0);
  const idCarrito = `combo-${combo.comboId}`;
  const yaEnCarrito = carrito.some((p) => p.artId === idCarrito);
  const mostrarAgregado = agregado || yaEnCarrito;

  const handleAgregarCarrito = () => {
    if (yaEnCarrito) return;

    agregarAlCarrito({
      artId: idCarrito,
      artNom: combo.comboNombre,
      artPrecio: combo.comboPrecio,
      artDescuento: combo.comboDescuento,
      artFoto: foto,
      esCombo: true,
      comboId: combo.comboId,
    });

    setAgregado(true);
    window.dispatchEvent(new Event('carrito-actualizado'));
  };

  const comboJsonLd = {
    '@context': 'https://schema.org',
    '@type': 'Product',
    name: combo.comboNombre,
    description: combo.comboDescrip,
    image: foto,
    offers: {
      '@type': 'Offer',
      url: absoluteUrl(`/combos/${combo.comboId}`),
      priceCurrency: 'MXN',
      price: precioFinal.toFixed(2),
      availability: 'https://schema.org/InStock',
    },
  };

  return (
    <section className="producto-page">
      <Seo
        title={combo.comboNombre}
        description={combo.comboDescrip}
        path={`/combos/${combo.comboId}`}
        image={foto}
        type="product"
        jsonLd={comboJsonLd}
      />

      <button className="back-btn" onClick={handleBackClick}>
        &#8249;
      </button>

      <div className="producto-detalle combo-detalle">
        <div className="producto-media combo-media">
          <img src={foto} alt={combo.comboNombre} className="producto-imagen combo-imagen" />
        </div>

        <div className="producto-info-detalle">
          <p className="producto-kicker">Combo</p>
          <h1>{combo.comboNombre}</h1>

          <div className="pag-precios">
            {combo.comboDescuento > 0 ? (
              <>
                <p className="pag-precio-original">${combo.comboPrecio.toFixed(2)}</p>
                <p className="pag-precio-descuento">${precioFinal.toFixed(2)}</p>
              </>
            ) : (
              <p className="pag-precio-normal">${precioFinal.toFixed(2)}</p>
            )}
          </div>

          <p className="producto-descripcion">{combo.comboDescrip}</p>

          <button
            className={`producto-buy-btn ${mostrarAgregado ? 'is-added' : ''}`}
            onClick={handleAgregarCarrito}
            disabled={mostrarAgregado}
          >
            {mostrarAgregado ? 'Ya está en el carrito' : 'Agregar combo al carrito'}
          </button>

          <ul className="producto-trust combo-trust">
            <li>Compra segura</li>
            <li>Atención personalizada</li>
            <li>Entrega local disponible</li>
          </ul>
        </div>

        {combo.productos.length > 0 && (
          <div className="combo-incluye">
            <div className="combo-incluye-head">
              <h3>Este combo incluye</h3>
              <span className="combo-incluye-conteo">
                {totalArticulos} {totalArticulos === 1 ? 'artículo' : 'artículos'}
              </span>
            </div>

            <div className="combo-incluye-mosaico">
              {combo.productos.map(({ producto, variante, cantidad }) => {
                const fotoItem = variante.artFoto?.[0] ?? variante.artFoto ?? '/img/default.jpg';

                return (
                  <div
                    key={`${producto.artId}-${variante.varId}`}
                    className="combo-incluye-item"
                    onClick={() =>
                      navigate(
                        variante.etiqueta
                          ? `${productoPath(producto)}?var=${variante.varId}`
                          : productoPath(producto)
                      )
                    }
                  >
                    <span className="combo-incluye-thumb">
                      <img src={fotoItem} alt={producto.artNom} loading="lazy" />
                      <span className="combo-incluye-cantidad">×{cantidad}</span>
                    </span>
                    <span className="combo-incluye-nombre">
                      {producto.artNom}
                      {variante.etiqueta ? ` - ${variante.etiqueta}` : ''}
                    </span>
                  </div>
                );
              })}
            </div>
          </div>
        )}
      </div>

      <section className="combo-otros">
        <CombosHome excluirComboId={combo.comboId} kicker="También te puede interesar" titulo="Otros combos" />
      </section>
    </section>
  );
};

export default PaginaCombo;
